import { LinearProgress } from '@mui/material';
import cn from 'classnames';
import { FC, ReactNode, useMemo } from 'react';
import { Area, AreaChart, ResponsiveContainer, Tooltip } from 'recharts';

import variables from '../../../styles/variables.module.scss';
import { getCurrencyStyle } from '../../../utils/color';
import { formatAmount } from '../../../utils/number';
import { UpIcon } from '../../icons';
// Constants
import { CurrenciesShortHand } from '../currencies/constants';

// Styles
import styles from './Card.module.scss';

export type CardProps = {
  currency_id: string;
  sub_title: string;
  title: string;
  total: number;
  className?: string;
  statusContent?: ReactNode;
  footerContent?: ReactNode;
  handleClick?: (currency_id: string) => void;
};

export const Card: FC<CardProps> = (props: CardProps) => {
  const { currency_id, total, title, sub_title, footerContent, className, statusContent, handleClick } = props;

  const currency = CurrenciesShortHand[currency_id] ?? currency_id;

  return (
    <div onClick={() => handleClick?.(currency_id)} className={cn('card', className, styles.container)}>
      <span style={getCurrencyStyle(currency_id)} className={styles.currency}>
        {currency}
      </span>
      <span className={styles.type}>
        {sub_title} <span>{title}</span>
      </span>
      <span className={cn('totalValue', styles.value)}>{formatAmount(total, currency)}</span>
      <div className={styles.status}>{statusContent}</div>
      {footerContent}
    </div>
  );
};

export const LoadingCard: FC = () => <LinearProgress className={[styles.container, styles.loadingCard].join(' ')} />;

export type CountModifyCardProps = Omit<CardProps, 'footerContent' | 'statusContent'> & {
  count_title: string;
  count: number;
  diagram?: number[];
  modify?: number;
  handleClick?: (currency_id: string) => void;
};

export const CountModifyCard: FC<CountModifyCardProps> = ({
  modify,
  count = 0,
  count_title,
  diagram,
  handleClick,
  ...props
}) => {
  const positiveDirection = (modify ?? 0) >= 0;
  const classList = [styles.diff, positiveDirection ? styles.positive : styles.negative];

  const cardCountValue = diagram ? diagram.length : count;

  const _cardAreaChartInit = 0;
  const cardAreaChartDiagramData = diagram?.length ? diagram : [0];

  const cardAreaChartData = [_cardAreaChartInit, ...cardAreaChartDiagramData];

  return (
    <Card
      statusContent={
        <>
          <span className={classList.join(' ')}>
            <UpIcon />
            {positiveDirection}
            {modify}%
          </span>
          <span className={cn('count', styles.count)}>
            {count_title} = <span className="value">{cardCountValue}</span>
          </span>
        </>
      }
      footerContent={
        <div className={styles.chart}>
          <ResponsiveContainer>
            <AreaChart data={cardAreaChartData} margin={{ bottom: 0, left: 0, right: 0, top: 0 }}>
              <defs>
                <linearGradient id="colorPositive" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="33.37%" stopColor="#16DB65" stopOpacity={1} />
                  <stop offset="100%" stopColor="rgba(22, 219, 101, 0)" />
                </linearGradient>
                <linearGradient id="colorNegative" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="33.37%" stopColor="#FF1700" stopOpacity={1} />
                  <stop offset="100%" stopColor="rgba(238, 50, 31, 0)" />
                </linearGradient>
              </defs>
              <Tooltip />
              <Area
                type="monotone"
                dataKey={(v) => v - 0} //fix fitting
                stroke={positiveDirection ? variables.Color_Green500 : variables.Color_Red500}
                fill={positiveDirection ? 'url(#colorPositive)' : 'url(#colorNegative)'}
              />
            </AreaChart>
          </ResponsiveContainer>
        </div>
      }
      handleClick={handleClick}
      {...props}
    />
  );
};

type CountCardProps = Omit<CardProps, 'footerContent' | 'statusContent'> & {
  count_title?: string;
  count: number;
  hiddenCount?: boolean;
  withStatusContent?: boolean;
};

type ExtendedCountCardProps = CountCardProps & {
  count_title_extended?: string;
  count_extended?: number;
  limit?: number;
};

export const CountCard: FC<ExtendedCountCardProps> = ({
  count_title,
  count_title_extended,
  className,
  count,
  count_extended,
  hiddenCount,
  withStatusContent = true,
  ...props
}) => {
  const countValue = useMemo(
    () => (
      <>
        <span>=</span> <span className="value">{count !== undefined && formatAmount(count)}</span>
      </>
    ),
    [count]
  );

  const countValueExtended = useMemo(
    () =>
      count_extended &&
      count_extended > 0 && (
        <>
          <span>=</span> <span className="value">{formatAmount(count_extended)}</span>
        </>
      ),
    [count_extended]
  );

  return (
    <Card
      {...props}
      className={cn(className, styles.countCard)}
      statusContent={
        withStatusContent && (
          <span className={'count'}>
            <span>
              {count_title} {!hiddenCount && countValue}
            </span>
            {count_title_extended && (
              <span>
                {', '}
                {count_title_extended} {!hiddenCount && countValueExtended}
              </span>
            )}
          </span>
        )
      }
    />
  );
};

export const ColoredCountCard: FC<ExtendedCountCardProps> = ({ className, ...props }) => {
  return (
    <CountCard
      {...props}
      className={cn(className, styles.coloredCard, {
        [styles.coloredCardBadTotal]:
          typeof props.count_extended !== 'undefined' && props.limit
            ? props.count + props.count_extended > props.limit
            : props.count < props.total,
      })}
    />
  );
};

export type TextCardProps = Omit<CardProps, 'footerContent' | 'statusContent'> & {
  text: string;
};

export const TextCard: FC<TextCardProps> = ({ text, ...props }) => {
  return <Card {...props} statusContent={<span className={styles.textContent}>{text}</span>} />;
};
